import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { Mail } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Fade } from '@/components/animate-ui/primitives/effects/fade'
import { Slide } from '@/components/animate-ui/primitives/effects/slide'
import { useExpiredLinkLanding } from '@/hooks/use-cases/useExpiredLinkLanding'
import { Skeleton } from '@/components/ui/skeleton'

const rExpiredSearchSchema = z.object({
  url: z.string().optional(),
})

export const Route = createFileRoute('/r/$rmdCalculationId')({
  validateSearch: rExpiredSearchSchema,
  component: ExpiredLinkPage,
})

function ExpiredLinkPage() {
  const { rmdCalculationId } = Route.useParams()
  const { url } = Route.useSearch()

  const { status, contact, isLoading } = useExpiredLinkLanding(
    rmdCalculationId,
    url,
  )

  // During an active redirect show nothing — the browser is already navigating.
  if (status === 'redirecting') {
    return null
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left panel — brand gradient (mirrors _auth/route.tsx) */}
      <div className="relative w-full md:w-1/2 min-h-[40vh] md:min-h-screen flex items-center justify-center p-8 md:p-12 overflow-hidden bg-gradient-to-b from-[#132e3f] via-[#7bbd91] to-[#5a9b7d]">
        <div className="relative z-10 text-center md:text-left max-w-lg">
          <Fade inView delay={0}>
            <div className="mb-8 flex justify-center md:justify-start">
              <img
                src={`${import.meta.env.BASE_URL.replace(/\/$/, '')}/q3-advisors-logo-dark.webp`}
                alt="Q3 Advisors"
                className="h-auto w-48 md:w-56 object-contain drop-shadow-lg"
              />
            </div>
          </Fade>
          <Slide direction="down" inView delay={100}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">
              RMD Report
            </h1>
          </Slide>
          <Slide direction="down" inView delay={200}>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Your Required Minimum Distribution analysis, delivered by your Q3
              Advisors team.
            </p>
          </Slide>
        </div>
      </div>

      {/* Right panel — expired card */}
      <div className="w-full md:w-1/2 flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <Fade inView delay={150}>
            <Card className="p-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-xl">This link has expired</CardTitle>
                <CardDescription className="text-sm leading-relaxed">
                  For a fresh copy of your RMD analysis report, please reach out
                  to your advisor:
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3">
                {isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-4 w-56" />
                  </div>
                ) : (
                  <Slide direction="up" inView delay={50}>
                    <div className="space-y-1">
                      <p className="font-medium text-foreground">
                        {contact?.advisorName ?? 'Q3 Advisors Team'}
                      </p>
                      <a
                        href={`mailto:${contact?.advisorEmail ?? 'assistance@q3adv.com'}`}
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>
                          {contact?.advisorEmail ?? 'assistance@q3adv.com'}
                        </span>
                      </a>
                    </div>
                  </Slide>
                )}
              </CardContent>
            </Card>
          </Fade>
        </div>
      </div>
    </div>
  )
}
